<template>
  <DefaultLayout>
    <div ref="topdiv">
      <img v-lazy="getPhoto" class="img-fluid" />
      <div class="text-center">
        <br />
        <h4>{{ getTitle }}</h4>
      </div>

      <div v-if="getDescription">
        <span v-html="getDescription" />
        <br />
        <br />
      </div>      

      <div id="question">
        <strong>{{ getCaseQuestion }}</strong>
        <br />
        <br />
      </div>

      <div class="text-center" v-if="getRPSCase().descriptionAboveCases">
        <span class="font-weight-bold">
          {{ getRPSCase().descriptionAboveCases }}
        </span>
      </div>

      <div class="text-center">
        <RPSChoices :selectedCaseNumber="currentCaseNumber" :disableClick="disableClick"/>
      </div>

      <div class="text-center" v-if="getRPSCase().descriptionBelowCases">
        <span class="font-weight-bold">
          {{ getRPSCase().descriptionBelowCases }}
        </span>
      </div>

      <base-progress
        v-if="showProgress()"
        type="default"
        :value="getProgress()"
        :label="$t('finishedplayers')"
      ></base-progress>

      <AnswersWaitingPlayers />
      <br />
    </div>

    <br />

    <SuggestionBoxInfo v-if="currentCaseNumber===1"/>

    <div class="text-center">
      <base-button
        size="sm"
        type="secondary"
        v-on:click="previousCase()"
        v-if="showPreviousCase"
        >{{ $t("previouscase") }}</base-button
      >

      <span v-if="!showNextCase && $store.getters.getIsFacilitator">
        <span
          id="disabled-wrapper"
          class="d-inline-block"
          tabindex="0"
          v-b-tooltip.hover
          :title="$t('next-button-tooltip-answers-missing')"
        >
          <base-button
            size="lg"
            type="success"
            v-on:click="nextCase()"
            disabled
            >{{ nextButtonText() }}</base-button
          >
        </span>
      </span>
      <span v-else-if="!showNextCase && $store.getters.getThisPlayer && $store.getters.getThisPlayer.paused">
        <span id="disabled-wrapper-paused-player" class="d-inline-block" tabindex="0" v-b-tooltip.hover :title="$t('paused-player-next-button-tooltip')">
          <base-button
            size="lg"
            type="success"
            v-on:click="nextCase()"
            disabled
            >{{ nextButtonText() }}</base-button
          >
        </span>
      </span>
      <span v-else-if="showNextCase">
        <base-button size="lg" type="success" v-on:click="nextCase()">{{
          nextButtonText()
        }}</base-button>
      </span>

      <LastPagePausedPlayerInfo v-if="showNextCase"/>      

      <br />
      {{ $t("case") }} {{ currentCaseNumber }} /
      {{ casesLength }}
    </div>
    <NewPlayerPausing />
    <Robot />
    <SuggestionBox :currentCaseNumber="currentCaseNumber" :isRpsGame="true"/>
  </DefaultLayout>
</template>

<script>
import DefaultLayout from "@/layouts/DefaultLayout.vue";
import RPSChoices from "@/components/rps/RPSChoices.vue";
import {
  updateGameCurrentCaseNumberForPlayers,
  removePlayerPause
} from "@/services/APIService";
import Robot from "@/components/Robot.vue";
import AnswersWaitingPlayers from "@/components/rps/AnswersWaitingPlayers.vue";
import NewPlayerPausing from "@/components/NewPlayerPausing.vue";
import { mapGetters } from "vuex";
import LastPagePausedPlayerInfo from "@/components/LastPagePausedPlayerInfo.vue";
import SuggestionBox from "@/components/SuggestionBox.vue";
import SuggestionBoxInfo from "@/components/SuggestionBoxInfo.vue";
import Swal from "sweetalert2";
import { convertPhotoToAmazonURL } from "@/services/utils";

export default {
  components: {
    DefaultLayout,
    Robot,
    AnswersWaitingPlayers,
    NewPlayerPausing,
    RPSChoices,
    LastPagePausedPlayerInfo,
    SuggestionBox,
    SuggestionBoxInfo,
  },
  data: function () {
    return {
      currentCaseNumber: 1,
      progressHasBeenDisabled: false,
      nextCaseEveryoneShown: false,
    };
  },
  watch: {
    getCurrentCaseNumberForPlayers: {
      handler(newData, oldData) {
        if (this.$store.state.main.game.reportingOpen) {
          // If reporting has been opened, facilitator wants to browse freely, players can't enter
          return;
        }
        if (
          this.$store.getters.getThisPlayer && this.$store.getters.getThisPlayer.paused && newData <= this.$store.getters.getNumberOfRPSCases
        ) {
          // Paused players don't proceed, UNLESS game proceeds to last page
          return;
        }

        if (this.currentCaseNumber < newData) {
          // We should move to next case
          this.$store.dispatch(
            "log",
            "New current case for players: " + newData
          );

          this.$store.commit({
            type: "setCurrentCaseNumber",
            currentCaseNumber: newData,
          });

          if (newData <= this.casesLength) {
            this.gotoCase(newData);
          } else {
            this.gotoEnd();
          }
        }
      },
    },
    getPlayers: {
      handler(newData, oldData) {
        // Let's check if this player was unpaused and is in another case than where the other players are.
        // If so, jump to the same case where the other players are.
        if (this.$store.getters.getThisPlayer && !this.$store.getters.getThisPlayer.paused) {
          if (this.$store.state.main.game.currentCaseNumberForPlayers !== this.currentCaseNumber) {
            this.$store.dispatch("log", "Player unpaused, taking back to current case for players");            
            this.$router.push("/rps/game/" + this.$store.state.main.game.currentCaseNumberForPlayers);
          }
        }
      },
      deep: true,
    },
  },
  async created() {
    if (this.$store.state.main.game.currentCaseNumberForPlayers > this.$store.getters.getNumberOfRPSCases) {
      // Throw also paused players to last page if game closed
      this.gotoEnd();
      return;
    }

    if (this.$store.state.main.game.reportingOpen) {
      // Player probably used back button -> push to reporting
      await this.$router.push("/rps/reporting");
      return;
    } 

    // Let's get case from URL
    let currentCaseNumber = parseInt(this.$route.params.case);

    if (!currentCaseNumber) {
      currentCaseNumber = 1;
    }

    if ((this.$store.getters.getIsFacilitator || this.$store.state.main.playerIsObserver) && this.$store.state.main.game.currentCaseNumberForPlayers !== currentCaseNumber) {
      await this.$router.push("/rps/game/" + this.$store.state.main.game.currentCaseNumberForPlayers);
      return;
    }

    if (this.$store.getters.getThisPlayer && !this.$store.getters.getThisPlayer.paused) {
      // Only paused players can be in a different case than others.   
       if (this.$store.state.main.game.currentCaseNumberForPlayers > this.$store.getters.getLastAnsweredCaseForPlayer + 1) {
        // This is a corner case:
        // - Other players are in case > 1
        // - A new player joins the game
        // - Because facilitator needs to use firewall hack, it may take a couple of seconds
        // before the new player is noticed and put on pause
        // - Because of this, let's check for non paused player that she is not going to
        // be taken too far away
        this.gotoCase(this.$store.getters.getLastAnsweredCaseForPlayer + 1);
        return;
      }

      if (currentCaseNumber !== this.$store.state.main.game.currentCaseNumberForPlayers) {
        this.gotoCase(this.$store.state.main.game.currentCaseNumberForPlayers);
        return;
      }
    }

    if (this.$store.getters.getThisPlayer && this.$store.getters.getThisPlayer.paused) {
      // Paused player
      const highestAnsweredCase = this.$store.getters.getLastAnsweredCaseForPlayer;

      // Let's check we don't go beyond the last case
      if (currentCaseNumber > highestAnsweredCase + 1) {
        let lastCase = this.$store.getters.getNumberOfRPSCases;
        if (highestAnsweredCase + 1 < lastCase) {
          lastCase = highestAnsweredCase + 1;
        }
        await this.$router.push("/rps/game/" + lastCase);
        return;
      }

      // Let's also check we don't go to a case too small:
      if (currentCaseNumber < this.$store.state.main.game.currentCaseNumberForPlayers &&
        highestAnsweredCase > currentCaseNumber) {
        await this.$router.push("/rps/game/" + highestAnsweredCase);
        return;
      }
    }
    
    this.$store.commit({
      type: "setCurrentCaseNumber",
      currentCaseNumber: currentCaseNumber,
    });

    this.currentCaseNumber = currentCaseNumber;

    this.$store.dispatch("connectNotification");

    this.$store.dispatch("updateGame");

    await this.$store.dispatch("updatePlayers");
    await this.$store.dispatch("updateRPSAnswers");

    this.checkAndUnpausePlayer();
  },
  computed: {
    ...mapGetters([
      "getCurrentCaseNumberForPlayers",
      "getPlayers",
    ]),
    disableClick() {
      return this.$store.state.main.playerIsOnlyFacilitator || this.$store.state.main.playerIsObserver
    },
    getRPSCases() {
      return this.$store.getters.getRPSCasesInCurrentLocale;
    },
    casesLength() {
      return this.getRPSCases.length;
    },
    getDescription() {
      return this.getRPSCase().description;
    },
    getTitle() {
      return this.getRPSCase().title;
    },
    getCaseQuestion() {
      return this.getRPSCase().caseQuestion;
    },
    getPhoto() {
      if (this.getRPSCase().photo) {
        return convertPhotoToAmazonURL(this.getRPSCase().photo);
      } else {
        return "/placeholder.png";
      }
    },
    isLastCase() {
      return this.casesLength === this.currentCaseNumber;
    },
    showNextCase() {
      // Non-paused player can not proceed if they are in the current case
      if (this.$store.state.main.playerIsObserver) {
        return false;
      }

      if (
        !this.$store.getters.getIsFacilitator &&
        !this.$store.getters.getThisPlayer.paused
      ) {
        return false;
      }

      // Paused player can proceed if answered this case:
      if (
        !this.$store.state.main.playerIsOnlyFacilitator &&
        this.$store.getters.getThisPlayer &&
        this.$store.getters.getThisPlayer.paused
      ) {
        return this.$store.getters.hasThisPlayerDoneRPSAnswerForCase(
          this.currentCaseNumber
        );
      }

      // Facilitator:
      return this.$store.getters.haveAllPlayersDoneRPSAnswer(this.currentCaseNumber);
    },
    showPreviousCase() {
      if (
        this.$store.getters.getCurrentCaseNumberForPlayers <
        this.currentCaseNumber
      ) {
        // Player has been paused but is now unpaused, but has proceeded. Let her
        // return until reaches the same case where other players are.
        return true;
      } else {
        return false;
      }
    },
  },
  methods: {
    nextButtonText() {
      if (
        this.$store.getters.getIsFacilitator &&
        !this.$store.state.main.playerIsObserver &&
        this.currentCaseNumber ===
          this.$store.getters.getNumberOfRPSCases
      ) {
        return this.$t("lastpagenextbuttontext");        
      } else if (
        this.nextCaseEveryoneShown ||
        (this.$store.getters.getIsFacilitator &&
        !this.$store.state.main.playerIsObserver &&
        this.currentCaseNumber ===
          this.$store.getters.getCurrentCaseNumberForPlayers)
      ) {
        // Let's use this flag, otherwise the "nextcase" text
        // will flash when currentCase is incresed when page is changed.
        this.nextCaseEveryoneShown = true;
        return this.$t("nextcaseeveryone");
      } else {
        return this.$t("nextcase");
      }
    },
    showProgress() {
     if (this.progressHasBeenDisabled) {
        return false;
      }
      if (
        this.$store.getters.haveAllPlayersDoneRPSAnswer(this.currentCaseNumber)
      ) {
        this.progressHasBeenDisabled = true;
        return false;
      } else {
        return true;
      }
    },
    getRPSCase() {
      return this.$store.getters.getRPSCaseByCaseNumber(this.currentCaseNumber);
    },
    getProgress() {
      const currentCase = this.currentCaseNumber;
      const unpausedPlayers = this.$store.getters.getNumberOfUnpausedPlayers;

      if (unpausedPlayers === 0) {
        // Avoid division by zero if there are no players
        return 0;
      }

      return Math.round(
        (100 *
          this.$store.getters.howManyUnpausedPlayersHaveDoneRPSAnswer(currentCase)) /
          unpausedPlayers
      );
    },
    async nextCase() {
      const newCase = this.currentCaseNumber + 1;
      if (
        this.$store.getters.getIsFacilitator &&
        !this.$store.state.main.playerIsObserver &&
        newCase > this.$store.state.main.game.currentCaseNumberForPlayers
      ) {
        await updateGameCurrentCaseNumberForPlayers(
          this.$store.state.main.game.id,
          this.$store.state.main.game.gameCode,
          newCase
        );
        await this.$store.dispatch("updateGame");
        // force update and wait for it, otherwise we will have old data for a while in the next case and "previous case" button will be shown briefly
      }

      if (this.isLastCase) {
        this.gotoEnd();
      } else {
        this.gotoCase(newCase);
      }
    },
    previousCase() {
      const newCase = this.currentCaseNumber - 1;

      this.$store.commit({
        type: "setCurrentCaseNumber",
        currentCaseNumber: newCase,
      });
      if (newCase === 0) {
        this.gotoBegin();
      } else {
        this.gotoCase(newCase);
      }
    },
    async gotoEnd() {
      if (this.$router.currentRoute.path !== "/rps/end") {
        await this.$router.push("/rps/end");
      }
    },
    async gotoCase(caseNumber) {
      // Prevent double navigation error
      if (this.$router.currentRoute.path !== "/rps/game/" + caseNumber) {
        await this.$router.push("/rps/game/" + caseNumber);
      }
    },
    async checkAndUnpausePlayer() {
      if (
        this.$store.getters.getThisPlayer && 
        this.$store.getters.getThisPlayer.paused &&
        this.currentCaseNumber === this.$store.getters.getCurrentCaseNumberForPlayers
      ) {
        await removePlayerPause(
          this.$store.getters.getThisPlayer.id,
          this.$store.state.main.game.gameCode
        );
        await this.$store.dispatch("updatePlayers");
        
        Swal.fire({
          text: this.$t("reached_others"),
          confirmButtonText: this.$t("ok"),
          allowOutsideClick: false
        });
      }
    },
  },
  async gotoBegin() {
    if (this.$router.currentRoute.path !== "/rps/begin") {
      await this.$router.push("/rps/begin");
    }
  },
};
</script>
<i18n>
{
  "en": {
    "nextcase": "Next",
    "nextcaseeveryone": "Forward (all will proceed)",
    "previouscase": "Previous case",
    "finishedplayers": "Answered",
    "case": "case",
    "next-button-tooltip-answers-missing": "All players have not yet answered.",
    "ok": "OK",
    "lastpagenextbuttontext": "Close the simulation (all will proceed)",
    "paused-player-next-button-tooltip": "Please answer the question first.",
    "reached_others": "You have reached the same case as other players. From now on, the facilitator will control proceeding in the simulation."
  },
  "fi": {
    "nextcase": "Eteenpäin",
    "nextcaseeveryone": "Eteenpäin (kaikki siirtyvät)",    
    "previouscase": "Edellinen tilanne",
    "finishedplayers": "Vastanneet",
    "case": "tilanne",
    "next-button-tooltip-answers-missing": "Kaikki pelaajat eivät ole vielä valinneet.",
    "ok": "OK",
    "lastpagenextbuttontext": "Sulje simulaatio (kaikki siirtyvät)",
    "paused-player-next-button-tooltip": "Vastaa kysymykseen ensin.",
    "reached_others": "Olet saavuttanut saman tilanteen kuin muut pelaajat. Pelinjohtaja vastaa tästä eteenpäin simulaatiossa etenemisestä."
  },
  "sv": {
    "nextcase": "Nästa",
    "nextcaseeveryone": "Framåt (alla går vidare)",    
    "previouscase": "Föregående",
    "finishedplayers": "De som svarat",
    "case": "fall",
    "next-button-tooltip-answers-missing": "Alla spelare har ännu inte valt.",
    "ok": "OK",
    "lastpagenextbuttontext": "Stäng simuleringen (alla går vidare)",
    "paused-player-next-button-tooltip": "Svara först på frågan.",
    "reached_others": "Du har nått samma fall som andra spelare. Från och med nu kommer facilitatorn att kontrollera hur simuleringen fortskrider."
  },
 "de": {
    "nextcase": "Weiter",
    "nextcaseeveryone": "Vorwärts (alle werden verschoben)",    
    "previouscase": "Zurück",
    "finishedplayers": "Beantwortet",
    "case": "Fall",
    "next-button-tooltip-answers-missing": "Alle Spieler haben noch nichts ausgewählt.",
    "ok": "Ok",
    "lastpagenextbuttontext": "Close the simulation (all will proceed)",
    "paused-player-next-button-tooltip": "Bitte beantworten Sie zuerst die Frage.",
    "reached_others": "Sie haben den gleichen Fall wie andere Spieler erreicht. Von nun an steuert der Spielleiter das Fortschreiten in der Simulation."
  },
  "ko": {
    "nextcase": "다음",
    "nextcaseeveryone": "다음 (전원 다음 주제로 이동)",
    "previouscase": "이전",
    "finishedplayers": "응답 완료",
    "case": "주제",
    "next-button-tooltip-answers-missing": "모든 플레이어가 아직 답변하지 않았습니다",
    "ok": "네" ,
    "lastpagenextbuttontext": "시뮬레이션 종료하기",
    "paused-player-next-button-tooltip": "먼저 질문에 답변해 주십시오.",
    "reached_others": "다른 플레이어와 동일한 주제에 도달했습니다. 이제부터 진행자가 시뮬레이션 진행을 제어합니다."
  }  
}
</i18n>
